import { useState } from 'react';
import { CheckCircle, ChevronLeft, ChevronRight, Circle } from 'react-feather';
import { Button as AriaButton } from 'react-aria-components';

import { DataStoreImage } from 'components/Image';
import { QueryResolver } from 'components/QueryResolver';
import { Button } from 'components/ui/button';
import { Dialog } from 'components/ui/dialog';
import { useWorkRequestChecklistsQuery } from 'services/api/checklists/endpoints';
import type {
  WorkRequestChecklist,
  WorkRequestChecklistItem,
} from 'services/api/checklists/types';
import { useLocale } from 'utils/hooks/useLocale';

type GalleryState = { photoIds: number[]; currentIndex: number };

export default function WorkRequestChecklists({
  workRequestId,
}: {
  workRequestId: number;
}) {
  const { t } = useLocale();
  const query = useWorkRequestChecklistsQuery(workRequestId, {
    skip: workRequestId === 0,
  });

  return (
    <QueryResolver query={query}>
      {(checklists) => {
        if (!checklists.length) return null;
        return (
          <fieldset className="mt-6 space-y-4">
            <legend className="text-base font-semibold text-gray-900">
              {t('Checklists')}
            </legend>
            {checklists.map((checklist) => (
              <ChecklistCard key={checklist.id} checklist={checklist} />
            ))}
          </fieldset>
        );
      }}
    </QueryResolver>
  );
}

function ChecklistCard({ checklist }: { checklist: WorkRequestChecklist }) {
  const { t } = useLocale();
  const [gallery, setGallery] = useState<GalleryState | null>(null);

  const total = checklist.items.length;
  const completed = checklist.items.filter((i) => i.checked).length;

  function openGallery(item: WorkRequestChecklistItem, index: number) {
    if (item.photoIds && item.photoIds.length > 0) {
      setGallery({ photoIds: item.photoIds, currentIndex: index });
    }
  }

  return (
    <>
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">
            {checklist.name}
          </h3>
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
            {completed} / {total} {t('complete')}
          </span>
        </div>
        {checklist.description && (
          <p className="px-4 py-2 text-xs text-gray-500">
            {checklist.description}
          </p>
        )}
        <ul className="divide-y divide-gray-50 px-4 py-2">
          {checklist.items.map((item) => (
            <li key={item.id} className="py-3">
              <div className="flex items-start gap-3">
                {item.checked ? (
                  <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                ) : (
                  <Circle className="mt-0.5 h-4 w-4 shrink-0 text-gray-300" />
                )}
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-medium text-gray-900">
                    {item.sortOrder != null && (
                      <span className="mr-1 text-gray-400">
                        {item.sortOrder}.
                      </span>
                    )}
                    {item.name}
                  </p>
                  {item.description && (
                    <p className="text-xs text-gray-500">{item.description}</p>
                  )}
                  {item.comment && (
                    <p className="text-xs italic text-gray-600">
                      {t('Comment')}: {item.comment}
                    </p>
                  )}
                  {item.photoIds && item.photoIds.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pt-1">
                      {item.photoIds.map((photoId, idx) => (
                        <AriaButton
                          key={photoId}
                          className="group shrink-0"
                          onPress={() => openGallery(item, idx)}
                        >
                          <DataStoreImage
                            imageId={photoId}
                            className="h-16 w-16 rounded object-cover group-data-[hovered]:opacity-75"
                          />
                        </AriaButton>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {gallery && (
        <Dialog
          isOpen
          onOpenChange={(open) => !open && setGallery(null)}
          size="5xl"
        >
          <div className="relative w-full overflow-hidden rounded-lg bg-black">
            <DataStoreImage
              imageId={gallery.photoIds[gallery.currentIndex]}
              className="max-h-[65vh] w-full object-contain"
            />
            {gallery.photoIds.length > 1 && (
              <>
                <div className="absolute inset-y-0 left-0 flex items-center p-1.5">
                  <Button
                    isDisabled={gallery.currentIndex === 0}
                    onClick={() =>
                      setGallery((g) =>
                        g ? { ...g, currentIndex: g.currentIndex - 1 } : g,
                      )
                    }
                  >
                    <ChevronLeft data-slot="icon" />
                    <span className="sr-only">Previous photo</span>
                  </Button>
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center p-1.5">
                  <Button
                    isDisabled={
                      gallery.currentIndex === gallery.photoIds.length - 1
                    }
                    onClick={() =>
                      setGallery((g) =>
                        g ? { ...g, currentIndex: g.currentIndex + 1 } : g,
                      )
                    }
                  >
                    <ChevronRight data-slot="icon" />
                    <span className="sr-only">Next photo</span>
                  </Button>
                </div>
                <div className="absolute right-0 top-0 p-1.5">
                  <span className="rounded-full bg-zinc-800/70 px-2 py-0.5 text-xs text-white">
                    {gallery.currentIndex + 1}/{gallery.photoIds.length}
                  </span>
                </div>
              </>
            )}
          </div>
        </Dialog>
      )}
    </>
  );
}
